import { Box, Link as MuiLink } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Link as RouterLink } from "react-router-dom";

import { FolderIcon } from "../doc-sidebar/icons.js";

/**
 * A folder page's sub-folders, listed as rows ABOVE its documents the way a
 * file explorer shows folders before files (2026-09-23). Each row opens the
 * sub-folder's own page. Sub-folders go two levels deep, so a sub-folder page
 * lists ITS sub-folders the same way, and the deepest tier renders none.
 * `count` is everything in the folder and below it.
 */
export function SubFolderList({
  folders,
}: {
  folders: ReadonlyArray<{ id: number; slug: string; name: string; count: number }>;
}): JSX.Element {
  return (
    <Box
      component="ul"
      data-testid="sub-folders"
      aria-label="Sub-folders"
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        overflow: "hidden",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t) => t.shadows[1],
      }}
    >
      {folders.map((f, i) => (
        <Box component="li" key={f.id} sx={i > 0 ? { borderTop: 1, borderColor: "divider" } : {}}>
          <MuiLink
            component={RouterLink}
            to={`/documentation/type/${encodeURIComponent(f.slug)}`}
            underline="none"
            data-testid={`sub-folder-${f.slug}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.25,
              color: "text.primary",
              "&:hover": { bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.6) },
            }}
          >
            <FolderIcon open={false} />
            <Box component="span" sx={{ flex: 1, minWidth: 0, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {f.name}
            </Box>
            <Box component="span" sx={{ fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
              {f.count} {f.count === 1 ? "document" : "documents"}
            </Box>
            <ChevronRightRoundedIcon fontSize="small" sx={{ color: "text.disabled" }} />
          </MuiLink>
        </Box>
      ))}
    </Box>
  );
}
